import React, { useMemo } from "react";
import { Map as MapComponent, Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useStore } from "@/store/store";

interface ZoneDetailMapProps {
  zoneResponse: GeoJSON.Feature | null; // The full GeoJSON Feature response from API
  className?: string;
}

// Component to render a single zone (similar to GeofenceItem)
const ZoneItem = ({ zoneResponse }: { zoneResponse: GeoJSON.Feature | null }) => {
  const geoJsonData = useMemo(() => {
    if (!zoneResponse) return null;
    
    return {
      type: 'FeatureCollection' as const,
      features: [zoneResponse]
    };
  }, [zoneResponse]);

  // Use consistent red color for all flight restriction zones
  const getZoneColor = () => {
    return '#ef4444'; // Red color for all zones
  };

  if (!geoJsonData) return null;

  const zoneColor = getZoneColor();

  const fillLayerStyle = {
    id: 'zone-fill',
    type: 'fill' as const,
    paint: {
      'fill-color': zoneColor,
      'fill-opacity': 0.3
    }
  };

  const outlineLayerStyle = {
    id: 'zone-outline',
    type: 'line' as const,
    paint: {
      'line-color': zoneColor,
      'line-width': 2
    }
  };

  return (
    <Source id="zone-source" type="geojson" data={geoJsonData}>
      <Layer {...fillLayerStyle} />
      <Layer {...outlineLayerStyle} />
    </Source>
  );
};

export const ZoneDetailMap: React.FC<ZoneDetailMapProps> = ({
  zoneResponse,
  className = "h-64 w-full"
}) => {
  const mapStyleUrl = useStore((state) => state.mapStyleUrl);

  // Calculate initial view state from the GeoJSON Feature
  const initialViewState = useMemo(() => {
    if (!zoneResponse?.geometry || 
        (zoneResponse.geometry.type !== 'Polygon' && zoneResponse.geometry.type !== 'MultiPolygon')) {
      return {
        longitude: -0.9,
        latitude: 53.5,
        zoom: 8
      };
    }

    // Calculate bounds for the geometry
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    const processCoordinates = (coords: number[] | number[][]) => {
      if (Array.isArray(coords)) {
        if (typeof coords[0] === 'number') {
          // This is a coordinate pair [lng, lat]
          const [lng, lat] = coords as number[];
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        } else {
          // This is an array of coordinates
          (coords as number[][]).forEach(processCoordinates);
        }
      }
    };

    // Process the geometry coordinates
    if (zoneResponse.geometry.type === 'MultiPolygon') {
      (zoneResponse.geometry.coordinates as number[][][][]).forEach((polygon: number[][][]) => {
        polygon.forEach((ring: number[][]) => {
          processCoordinates(ring);
        });
      });
    } else if (zoneResponse.geometry.type === 'Polygon') {
      (zoneResponse.geometry.coordinates as number[][][]).forEach((ring: number[][]) => {
        processCoordinates(ring);
      });
    }

    const centerLng = (minLng + maxLng) / 2;
    const centerLat = (minLat + maxLat) / 2;
    
    // Calculate appropriate zoom based on zone size
    const lngDiff = maxLng - minLng;
    const latDiff = maxLat - minLat;
    const maxDiff = Math.max(lngDiff, latDiff);
    
    let zoom = 14;
    if (maxDiff < 0.01) zoom = 16;
    else if (maxDiff < 0.05) zoom = 15;
    else if (maxDiff < 0.1) zoom = 14;
    else zoom = 12;
    
    return {
      longitude: centerLng,
      latitude: centerLat,
      zoom: zoom
    };
  }, [zoneResponse]);

  if (!zoneResponse) {
    return (
      <div className={`${className} rounded-lg border bg-muted flex items-center justify-center`}>
        <div className="text-center text-muted-foreground">
          <p className="text-sm">No zone data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} rounded-lg border overflow-hidden`}>
      <MapComponent
        id="zone-detail-map"
        mapStyle={mapStyleUrl}
        initialViewState={initialViewState}
        style={{ width: "100%", height: "100%" }}
        mapLib={import('maplibre-gl')}
        attributionControl={false}
        reuseMaps
      >
        <ZoneItem zoneResponse={zoneResponse} />
      </MapComponent>
    </div>
  );
};
